import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Sliders, Sparkles, RefreshCw } from "lucide-react";

// ─── Palette ────────────────────────────────────────────────────────────────
// #0B3D2E  deep forest   (darkest bg / shadow base)
// #145A43  mid forest    (card bg / border)
// #2F8F6F  sage green    (primary accent / active states)
// #D7C7A1  warm sand     (value readouts / highlights)
// #0F0F0F  near-black    (canvas bg)
// ────────────────────────────────────────────────────────────────────────────

const CreativePlayground = () => {
    const [scale, setScale] = useState(140);
    const [speed, setSpeed] = useState(2.5);
    const [fluidity, setFluidity] = useState(20);
    const [hue, setHue] = useState(160); // ← default hue shifted to green range (was 270 purple)
    const [presetName, setPresetName] = useState("Forest Drift");

    const pathRef = useRef(null);
    const stop1Ref = useRef(null);
    const stop2Ref = useRef(null);
    const panelRef = useRef(null);

    const paramsRef = useRef({ scale, speed, fluidity, hue });

    useEffect(() => {
        paramsRef.current = { scale, speed, fluidity, hue };
    }, [scale, speed, fluidity, hue]);

    // Presets — renamed + hues shifted to green/earth range
    const presets = {
        "Forest Drift": { scale: 140, speed: 2.0, fluidity: 15, hue: 155 }, // was "Cosmic Calm"  hue 260
        "Rapid Current": { scale: 155, speed: 7.5, fluidity: 32, hue: 175 }, // was "Hyper-drive"  hue 325
        "Still Waters": { scale: 130, speed: 0.8, fluidity: 10, hue: 185 }, // was "Deep Sea"     hue 185 (kept — already teal)
        "Canopy Burn": { scale: 165, speed: 4.5, fluidity: 25, hue: 35 }, // was "Supernova"    hue 20  (warm sand / amber)
    };

    const applyPreset = (name) => {
        setPresetName(name);
        const p = presets[name];

        let steps = 25;
        let cStep = 0;
        const startScale = scale;
        const startSpeed = speed;
        const startFluidity = fluidity;
        const startHue = hue;

        const lerpAnimate = () => {
            cStep++;
            const t = cStep / steps;
            const ease = t * (2 - t);

            setScale(parseFloat((startScale + (p.scale - startScale) * ease).toFixed(1)));
            setSpeed(parseFloat((startSpeed + (p.speed - startSpeed) * ease).toFixed(2)));
            setFluidity(parseFloat((startFluidity + (p.fluidity - startFluidity) * ease).toFixed(1)));

            let hueDiff = p.hue - startHue;
            if (hueDiff > 180) hueDiff -= 360;
            if (hueDiff < -180) hueDiff += 360;
            setHue(Math.floor((startHue + hueDiff * ease + 360) % 360));

            if (cStep < steps) requestAnimationFrame(lerpAnimate);
        };
        requestAnimationFrame(lerpAnimate);
    };

    // 120fps animation loop — unchanged logic, only colors updated
    useEffect(() => {
        let phase = 0;
        let animId;

        const getBlobPath = (size, waveAmp, phase) => {
            const center = 200;
            const numPoints = 8;
            const points = [];
            const complexity = 3;

            for (let i = 0; i < numPoints; i++) {
                const angle = (i * 2 * Math.PI) / numPoints;
                const r = size + Math.sin(angle * complexity + phase) * waveAmp;
                points.push({
                    x: center + r * Math.cos(angle),
                    y: center + r * Math.sin(angle),
                });
            }

            let d = `M ${points[0].x} ${points[0].y}`;
            for (let i = 0; i < numPoints; i++) {
                const prev = points[(i - 1 + numPoints) % numPoints];
                const curr = points[i];
                const next = points[(i + 1) % numPoints];
                const next2 = points[(i + 2) % numPoints];

                const cp1x = curr.x + (next.x - prev.x) * 0.15;
                const cp1y = curr.y + (next.y - prev.y) * 0.15;
                const cp2x = next.x - (next2.x - curr.x) * 0.15;
                const cp2y = next.y - (next2.y - curr.y) * 0.15;

                d += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${next.x} ${next.y}`;
            }
            return d + " Z";
        };

        const tick = () => {
            const { scale: size, speed: spd, fluidity: amp, hue: col } = paramsRef.current;
            phase += spd * 0.015;

            if (pathRef.current) {
                pathRef.current.setAttribute("d", getBlobPath(size, amp, phase));
            }
            if (stop1Ref.current) {
                // Gradient stop 1: the hue itself (sage / teal range by default)
                stop1Ref.current.setAttribute("stop-color", `hsl(${col}, 80%, 45%)`);
                // ↑ was hsl(col, 95%, 60%) — reduced lightness for darker earthy tone
            }
            if (stop2Ref.current) {
                // Gradient stop 2: offset +50 toward warm sand territory (was +80)
                stop2Ref.current.setAttribute("stop-color", `hsl(${(col + 50) % 360}, 70%, 35%)`);
                // ↑ was hsl((col+80)%360, 90%, 50%)
            }
            if (panelRef.current) {
                // Shadow base: deep forest instead of purple ← was hsl(col, 70%, 15%)
                panelRef.current.style.boxShadow = `0 25px 70px -10px hsl(${col}, 50%, 10%)`;
                // Border: sage-tinted ← was hsla(col, 60%, 40%, 0.35)
                panelRef.current.style.borderColor = `hsla(${col}, 45%, 35%, 0.35)`;
            }

            animId = requestAnimationFrame(tick);
        };

        animId = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(animId);
    }, []);

    return (
        <section className="relative py-24 bg-[#0F0F0F] overflow-hidden">
            <div className="max-w-6xl mx-auto px-6 sm:px-10 lg:px-12">

                <div className="text-center mb-16">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5 }}
                        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#0B3D2E]/50 border border-[#2F8F6F]/20 text-[#2F8F6F] text-sm font-semibold mb-4"

                    >
                        <Sparkles className="w-4 h-4" /> Visual Sandbox
                    </motion.div>

                    <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-white mb-4">
                        Creative <span className="text-[#2F8F6F]">Playground</span>
                    </h2>
                    <p className="text-slate-400 text-lg max-w-2xl mx-auto">
                        Morph visual states and tweak mathematical parameters in real-time. Highlights frontend physics engineering.
                    </p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">

                    <div className="lg:col-span-6 flex justify-center">
                        <div
                            ref={panelRef}
                            className="relative flex items-center justify-center h-[360px] w-[360px] sm:h-[400px] sm:w-[400px] bg-[#0B3D2E]/10 backdrop-blur-md border border-[#145A43]/40 rounded-3xl overflow-hidden transition-all duration-300"

                        >
                            <svg className="w-full h-full" viewBox="0 0 400 400">
                                <defs>
                                    <linearGradient id="blobGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                                        <stop ref={stop1Ref} offset="0%" stopColor="hsl(155, 80%, 45%)" />
                                        <stop ref={stop2Ref} offset="100%" stopColor="hsl(35, 70%, 55%)" />
                                    </linearGradient>
                                </defs>
                                <path
                                    ref={pathRef}
                                    d=""
                                    fill="url(#blobGrad)"
                                    className="transition-all duration-300 ease-out filter drop-shadow-[0_15px_30px_rgba(47,143,111,0.35)]"
                                />
                            </svg>

                            <div className="absolute flex flex-col items-center select-none pointer-events-none text-center">
                                <span className="text-white/20 uppercase tracking-widest text-xs font-bold mb-1">armppy physics</span>
                                <span className="text-white/60 font-black text-2xl">iki.</span>
                            </div>
                        </div>
                    </div>

                    <div className="lg:col-span-6 bg-[#0B3D2E]/20 backdrop-blur-md border border-[#145A43]/40 p-6 sm:p-8 rounded-3xl">

                        <div className="flex items-center gap-2 mb-6">
                            <Sliders className="w-5 h-5 text-[#2F8F6F]" />
                            <h3 className="text-xl font-bold text-white">Parameters Panel</h3>
                        </div>

                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-8">
                            {Object.keys(presets).map((name) => (
                                <button
                                    key={name}
                                    onClick={() => applyPreset(name)}
                                    className={`px-3 py-2 rounded-xl text-xs sm:text-sm font-semibold transition-all duration-300 ${presetName === name
                                        ? "bg-[#2F8F6F] text-white"
                                        : "bg-[#0B3D2E]/40 text-[#2F8F6F]/70 hover:bg-[#145A43]/60 hover:text-white"
                                        }`}

                                >
                                    {name}
                                </button>
                            ))}
                        </div>

                        <div className="space-y-6">

                            <div>
                                <div className="flex justify-between text-sm mb-2">
                                    <span className="text-slate-400 font-medium">Blob Base Scale</span>
                                    <span className="text-[#D7C7A1] font-bold">{scale}px</span>
                                </div>
                                <input
                                    type="range" min="90" max="180" value={scale}
                                    onChange={(e) => { setScale(parseFloat(e.target.value)); setPresetName(""); }}
                                    className="w-full accent-[#2F8F6F] h-2 bg-[#0B3D2E]/60 rounded-lg cursor-pointer"
                                />
                            </div>

                            <div>
                                <div className="flex justify-between text-sm mb-2">
                                    <span className="text-slate-400 font-medium">Fluidity (Wave Height)</span>
                                    <span className="text-[#D7C7A1] font-bold">{fluidity}px</span>
                                </div>
                                <input
                                    type="range" min="2" max="45" value={fluidity}
                                    onChange={(e) => { setFluidity(parseFloat(e.target.value)); setPresetName(""); }}
                                    className="w-full accent-[#2F8F6F] h-2 bg-[#0B3D2E]/60 rounded-lg cursor-pointer"
                                />
                            </div>

                            <div>
                                <div className="flex justify-between text-sm mb-2">
                                    <span className="text-slate-400 font-medium">Morphing Frequency (Speed)</span>
                                    <span className="text-[#D7C7A1] font-bold">{speed}Hz</span>
                                </div>
                                <input
                                    type="range" min="0" max="10" step="0.1" value={speed}
                                    onChange={(e) => { setSpeed(parseFloat(e.target.value)); setPresetName(""); }}
                                    className="w-full accent-[#2F8F6F] h-2 bg-[#0B3D2E]/60 rounded-lg cursor-pointer"
                                />
                            </div>

                            <div>
                                <div className="flex justify-between text-sm mb-2">
                                    <span className="text-slate-400 font-medium">Spectral Color Hue</span>
                                    <span className="font-bold" style={{ color: `hsl(${hue}, 80%, 60%)` }}>{hue}°</span>
                                </div>
                                <input
                                    type="range" min="0" max="360" value={hue}
                                    onChange={(e) => { setHue(parseInt(e.target.value)); setPresetName(""); }}
                                    className="w-full h-2 bg-[#0B3D2E]/60 rounded-lg cursor-pointer accent-[#2F8F6F]"
                                />
                            </div>
                        </div>

                        <div className="flex justify-end mt-8">
                            <button
                                onClick={() => applyPreset("Forest Drift")}
                                className="flex items-center gap-2 text-xs font-bold text-[#2F8F6F]/50 hover:text-[#D7C7A1] transition-colors duration-300"
                            >
                                <RefreshCw className="w-3 h-3" /> Reset Defaults
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default CreativePlayground;